import React, { ElementType } from 'react';
import {
  Box,
  HStack,
  IconButton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon, ViewIcon } from '@chakra-ui/icons';
import { FiImage } from 'react-icons/fi';
import { CatalogueItem } from '../../utils/types';
import {
  CatalogueEmptyState,
  CatalogueStatusBadge,
  catalogueBorder,
  catalogueSurface,
  formatCurrency,
  formatDateLabel,
} from './components/catalogueUi';

type Props = {
  products: CatalogueItem[];
  orgId?: string;
  catalogueId: string;
  onOpenItem: (itemId: string) => void;
  onEditItem: (itemId: string) => void;
  onDeleteItem: (item: CatalogueItem) => void;
};

const CatalogueTable: React.FC<Props> = ({
  products,
  onDeleteItem,
  onEditItem,
  onOpenItem,
}) => {
  if (!products.length) {
    return (
      <CatalogueEmptyState
        title="No catalogue items found"
        description="Try clearing your filters or add a new item to start building your catalogue."
        icon={FiImage as ElementType}
      />
    );
  }

  return (
    <TableContainer
      bg={catalogueSurface}
      borderWidth="1px"
      borderColor={catalogueBorder}
      borderRadius="28px"
      overflow="hidden"
      boxShadow="0 18px 44px rgba(15, 23, 42, 0.05)"
    >
      <Table variant="simple" size="md">
        <Thead bg="gray.50">
          <Tr>
            <Th py={4}>Item</Th>
            <Th py={4}>Status</Th>
            <Th py={4}>Price</Th>
            <Th py={4}>Description</Th>
            <Th py={4}>Updated</Th>
            <Th py={4} textAlign="right">
              Actions
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {products.map((product) => (
            <Tr
              key={product.id}
              _hover={{ bg: 'gray.50' }}
              transition="background 0.16s ease"
            >
              <Td py={4}>
                <HStack
                  spacing={4}
                  align="center"
                  cursor="pointer"
                  onClick={() => onOpenItem(product.id)}
                >
                  <Box
                    w="72px"
                    h="72px"
                    borderRadius="18px"
                    overflow="hidden"
                    bg="gray.100"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    {product.images?.[0] ? (
                      <img
                        src={product.images[0].image}
                        alt={product.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Box as={FiImage as ElementType} boxSize={7} color="gray.400" />
                    )}
                  </Box>
                  <VStack spacing={1} align="flex-start">
                    <Text fontWeight="800" color="gray.900">
                      {product.name}
                    </Text>
                    <Text fontSize="sm" color="gray.500" noOfLines={1} maxW="260px">
                      {product.images?.length
                        ? `${product.images.length} image${product.images.length > 1 ? 's' : ''}`
                        : 'No product images'}
                    </Text>
                  </VStack>
                </HStack>
              </Td>
              <Td py={4}>
                <CatalogueStatusBadge
                  isActive={Boolean(product.is_active)}
                  imageCount={product.images?.length}
                />
              </Td>
              <Td py={4}>
                <Text fontWeight="700" color="gray.900">
                  {formatCurrency(product.price)}
                </Text>
              </Td>
              <Td py={4}>
                <Text color="gray.600" noOfLines={2} maxW="320px">
                  {product.description || 'No description provided yet.'}
                </Text>
              </Td>
              <Td py={4}>
                <Text color="gray.600">
                  {formatDateLabel(product.updated_at || product.created_at)}
                </Text>
              </Td>
              <Td py={4}>
                <HStack justify="flex-end" spacing={1}>
                  <Tooltip label="Open item" hasArrow>
                    <IconButton
                      aria-label="Open item"
                      icon={<ViewIcon />}
                      variant="ghost"
                      onClick={() => onOpenItem(product.id)}
                    />
                  </Tooltip>
                  <Tooltip label="Edit item" hasArrow>
                    <IconButton
                      aria-label="Edit item"
                      icon={<EditIcon />}
                      variant="ghost"
                      onClick={() => onEditItem(product.id)}
                    />
                  </Tooltip>
                  <Tooltip label="Delete item" hasArrow>
                    <IconButton
                      aria-label="Delete item"
                      icon={<DeleteIcon />}
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => onDeleteItem(product)}
                    />
                  </Tooltip>
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default CatalogueTable;
